import React from "react";
import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { ytId } from "./api";

export const T = {
  forest: "inline-flex items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ivory transition-[background-color,transform] duration-300 hover:bg-forest-deep hover:-translate-y-0.5 cursor-pointer",
  gold: "inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-ink transition-[background-color,transform] duration-300 hover:bg-gold-bright hover:-translate-y-0.5 cursor-pointer",
  outline: "inline-flex items-center justify-center gap-2 rounded-full border border-forest/30 dark:border-whisper/30 px-8 py-4 text-[11px] font-semibold tracking-[0.25em] uppercase text-forest dark:text-whisper hover:border-gold hover:text-gold transition-colors duration-300 cursor-pointer",
  input: "w-full rounded-xl border border-beige dark:border-charcoal bg-ivory/80 dark:bg-night/80 px-4 py-3 text-sm text-ink dark:text-whisper placeholder:text-stone/60 focus:outline-none focus:border-gold transition-colors",
  card: "rounded-2xl border border-beige dark:border-charcoal bg-sand/60 dark:bg-onyx/60 p-6 sm:p-8",
  adminTab: "px-4 py-2 rounded-full text-[11px] tracking-[0.14em] uppercase transition-colors cursor-pointer",
};

export const TEyebrow = ({ children }) => (
  <div className="flex items-center justify-center gap-3 mb-6">
    <span className="h-px w-10 bg-gold" />
    <span className="text-[11px] tracking-[0.35em] uppercase text-gold">{children}</span>
    <span className="h-px w-10 bg-gold" />
  </div>
);

export const TTitle = ({ eyebrow, title, lead, testid }) => (
  <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16" data-testid={testid}>
    {eyebrow && <TEyebrow>{eyebrow}</TEyebrow>}
    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.12] text-ink dark:text-whisper">{title}</h2>
    {lead && <p className="mt-4 text-base sm:text-lg text-stone dark:text-mist leading-relaxed">{lead}</p>}
  </div>
);

export const TField = ({ label, required, children, className = "" }) => (
  <label className={`block text-left ${className}`}>
    <span className="block text-[11px] tracking-[0.18em] uppercase text-stone dark:text-mist mb-1.5">
      {label}{required ? " *" : ""}
    </span>
    {children}
  </label>
);

export const TConsent = ({ checked, onChange, text, testid }) => (
  <label className="flex items-start gap-3 text-left cursor-pointer">
    <input type="checkbox" data-testid={testid} checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-4 h-4 accent-[#2d4a3e]" />
    <span className="text-xs text-stone dark:text-mist leading-relaxed">{text}</span>
  </label>
);

export const TBadge = ({ children, testid }) => (
  <span data-testid={testid} className="inline-flex items-center rounded-full border border-gold/60 bg-gold/10 text-gold px-4 py-1.5 text-[10px] tracking-[0.28em] uppercase">
    {children}
  </span>
);

export const THeader = ({ right }) => (
  <header className="sticky top-0 z-40 bg-ivory/90 dark:bg-night/90 backdrop-blur border-b border-beige dark:border-charcoal">
    <div className="max-w-6xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between gap-4">
      <Link to="/" data-testid="tournee-home-link" className="font-serif text-xl sm:text-2xl text-ink dark:text-whisper">
        Le Guérisseur <span className="text-gold text-sm align-middle tracking-[0.2em] uppercase">· La Tournée</span>
      </Link>
      <div className="flex items-center gap-4">
        <Link to="/" data-testid="tournee-site-link" className="hidden sm:inline text-[11px] tracking-[0.18em] uppercase text-stone dark:text-mist hover:text-gold transition-colors">
          le-guerisseur.com
        </Link>
        <Link
          to="/tournee/admin"
          data-testid="tournee-admin-link"
          aria-label="Accéder au back-office de la Tournée"
          title="Admin"
          className="h-9 w-9 rounded-full border border-beige dark:border-charcoal hover:border-forest dark:hover:border-gold flex items-center justify-center transition-colors text-stone dark:text-mist hover:text-forest dark:hover:text-gold"
        >
          <Shield size={13} strokeWidth={1.5} />
        </Link>
        {right}
      </div>
    </div>
  </header>
);

export const TFooter = ({ settings }) => {
  const socials = settings?.socials || {};
  const entries = Object.entries(socials).filter(([, v]) => v);
  return (
    <footer data-testid="tournee-footer" className="bg-night text-mist py-14">
      <div className="max-w-6xl mx-auto px-6 text-center space-y-6">
        <Link to="/" data-testid="tournee-footer-home" className="font-serif text-2xl text-whisper hover:text-gold transition-colors">
          Le Guérisseur
        </Link>
        {entries.length > 0 && (
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em]">
            {entries.map(([name, url]) => (
              <a key={name} data-testid={`tournee-social-${name}`} href={url} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors">
                {name}
              </a>
            ))}
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs text-mist/80">
          <details className="max-w-md">
            <summary data-testid="tournee-mentions-toggle" className="cursor-pointer hover:text-gold transition-colors">Mentions légales</summary>
            <p className="mt-2 text-mist/70 leading-relaxed">{settings?.mentions_legales}</p>
          </details>
          <details className="max-w-md">
            <summary data-testid="tournee-confidentialite-toggle" className="cursor-pointer hover:text-gold transition-colors">Confidentialité</summary>
            <p className="mt-2 text-mist/70 leading-relaxed">{settings?.confidentialite}</p>
          </details>
        </div>
        <p className="text-xs text-mist/50">© {new Date().getFullYear()} Le Guérisseur — La Tournée · <a href="https://le-guerisseur.com" className="hover:text-gold">le-guerisseur.com</a></p>
      </div>
    </footer>
  );
};

export const YtEmbed = ({ url, title, testid }) => {
  const id = ytId(url);
  if (!id) return null;
  return (
    <div data-testid={testid} className="rounded-2xl overflow-hidden border border-beige dark:border-charcoal bg-onyx aspect-video">
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        title={title || "Vidéo YouTube"}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};
